import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card } from '../../../components/ui/card';
import BackButton from '../../../components/admin/BackButton';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking'); // checking, success, failed
  const [paymentData, setPaymentData] = useState(null);
  const [error, setError] = useState(null);
  const [pollCount, setPollCount] = useState(0);

  const sessionId = searchParams.get('session_id');
  const maxPolls = 5;
  const pollInterval = 2000; // 2 seconds

  useEffect(() => {
    if (!sessionId) {
      setStatus('failed');
      setError('No session ID found');
      return;
    }

    checkPaymentStatus();
  }, [sessionId, pollCount]);

  const checkPaymentStatus = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(
        `${API_URL}/api/finance/stripe/status/${sessionId}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      const data = response.data;
      setPaymentData(data);

      if (data.payment_status === 'paid') {
        setStatus('success');
      } else if (data.status === 'expired') {
        setStatus('failed');
        setError('Payment session expired');
      } else if (pollCount < maxPolls) {
        // Continue polling
        setTimeout(() => {
          setPollCount(prev => prev + 1);
        }, pollInterval);
      } else {
        setStatus('failed');
        setError('Payment status check timed out. Please check your email for confirmation.');
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
      setStatus('failed');
      setError(error.response?.data?.detail || 'Failed to check payment status');
    }
  };

  const handleBackToInvoice = () => {
    if (paymentData?.invoice_id) {
      navigate(`/admin/finance/invoices/${paymentData.invoice_id}`);
    } else {
      navigate('/admin/finance/invoices');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <BackButton />

      <div className="max-w-2xl mx-auto mt-16">
        <Card className="p-8 text-center">
          {status === 'checking' && (
            <>
              <Loader2 className="w-16 h-16 mx-auto mb-4 text-[#00B4D8] animate-spin" />
              <h1 className="text-2xl font-bold text-[#2B2D42] mb-2">
                Processing Payment...
              </h1>
              <p className="text-gray-600">
                Please wait while we confirm your payment with Stripe.
              </p>
              <p className="text-sm text-gray-500 mt-4">
                Attempt {pollCount + 1} of {maxPolls}
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <CheckCircle className="w-16 h-16 mx-auto mb-4 text-green-500" />
              <h1 className="text-2xl font-bold text-[#2B2D42] mb-2">
                Payment Successful!
              </h1>
              <p className="text-gray-600 mb-6">
                Thank you for your payment. Your invoice has been marked as paid.
              </p>

              {paymentData && (
                <div className="bg-gray-50 p-4 rounded-lg mb-6 text-left">
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <span className="text-gray-600">Amount:</span>
                    <span className="font-semibold">
                      {paymentData.currency} {(paymentData.amount_total / 100).toFixed(2)}
                    </span>

                    <span className="text-gray-600">Invoice ID:</span>
                    <span className="font-semibold">{paymentData.invoice_id}</span>

                    <span className="text-gray-600">Session ID:</span>
                    <span className="font-mono text-xs">{paymentData.session_id}</span>
                  </div>
                </div>
              )}

              <Button
                onClick={handleBackToInvoice}
                className="bg-[#00B4D8] hover:bg-[#023E8A] text-white"
              >
                Back to Invoice
              </Button>
            </>
          )}

          {status === 'failed' && (
            <>
              <XCircle className="w-16 h-16 mx-auto mb-4 text-red-500" />
              <h1 className="text-2xl font-bold text-[#2B2D42] mb-2">
                Payment Status Check Failed
              </h1>
              <p className="text-gray-600 mb-4">{error}</p>

              <div className="flex gap-3 justify-center">
                <Button
                  variant="outline"
                  onClick={() => window.location.reload()}
                >
                  Try Again
                </Button>
                <Button
                  onClick={() => navigate('/admin/finance/invoices')}
                  className="bg-[#00B4D8] hover:bg-[#023E8A] text-white"
                >
                  Back to Invoices
                </Button>
              </div>
            </>
          )}
        </Card>
      </div>
    </div>
  );
};

export default PaymentSuccess;
